import { type Dayjs, type ManipulateType, type UnitType } from 'dayjs';

import { timeOfDayConfig, TimeOfDayEnum } from '../data/time';
import dayjs from './dayjs';

// Current Date in DayJS
export const getCurrentDate = (): Dayjs => dayjs();

// to start of the day
export const toStartOfDay = (date: Dayjs): Dayjs => date.startOf('day');

// to end of the day
export const toEndOfDay = (date: Dayjs): Dayjs => date.endOf('day');

// Tomorrow's Date
export const getTomorrowDate = (): Dayjs => dayjs().add(1, 'day');

// This Week (from today to end of the week)
export const getThisWeek = (): [Dayjs, Dayjs] => {
  const today = dayjs();
  const endOfWeek = today.weekday(7);

  return [today, endOfWeek];
};

// Start of the Month
export const getStartOfMonth = (): Dayjs => dayjs().startOf('month');

// End of the Month
export const getEndOfMonth = (): Dayjs => dayjs().endOf('month');

// Next Month (from start of next month to end of next month)
export const getNextMonth = (): [Dayjs, Dayjs] => {
  const startOfNextMonth = dayjs().add(1, 'month').startOf('month');
  const endOfNextMonth = startOfNextMonth.endOf('month');

  return [startOfNextMonth, endOfNextMonth];
};

export const transformDatesInObject = (obj: Record<string, any>): Record<string, any> => {
  const transformedObj = { ...obj };

  Object.keys(obj).forEach((key) => {
    if (dayjs.isDayjs(obj[key])) {
      transformedObj[key] = obj[key].toISOString();
    } else if (typeof obj[key] === 'object') {
      if (!obj[key]) {
        transformedObj[key] = obj[key];
      } else {
        transformedObj[key] = transformDatesInObject(obj[key]);
      }
    }
  });

  return transformedObj;
};

export const ceilDate = (
  date: Dayjs,
  unit: UnitType,
  amount: number,
) => date.add(amount - (date.get(unit) % amount), unit as ManipulateType).startOf(unit);

export const getTimeOfDay = (time = dayjs()): TimeOfDayEnum => {
  const hour = time.hour();

  const timeOfDay = Object.keys(timeOfDayConfig).find((key) => {
    const { start, end } = timeOfDayConfig[key as TimeOfDayEnum];
    return hour >= start && hour < end;
  });

  return timeOfDay as TimeOfDayEnum || TimeOfDayEnum.Night;
};

export const getTimeSince = (actionTime: string | Dayjs): [number, string][] => {
  const now = dayjs();
  const actionTimestamp = dayjs(actionTime);
  const diff = now.diff(actionTimestamp);

  if (diff < 0) return [[0, 'hr'], [0, 'min'], [0, 'sec']];

  const durationObj = dayjs.duration(diff);

  const days = Math.floor(durationObj.asDays());
  const hours = Math.floor(durationObj.asHours() % 24);
  const minutes = Math.floor(durationObj.asMinutes() % 60);
  const seconds = Math.floor(durationObj.asSeconds() % 60);

  if (days > 0) {
    return [
      [days, 'day'],
      [hours, 'hr'],
      [minutes, 'min'],
    ];
  }

  return [
    [hours, 'hr'],
    [minutes, 'min'],
    [seconds, 'sec'],
  ];
};
